
import { supabase } from '@/integrations/supabase/client';
import { Event, EventWithStatus } from '@/types/event';
import { Database } from '@/integrations/supabase/types';

type EventsInsert = Database['public']['Tables']['events']['Insert'];

export const fetchEvents = async (): Promise<EventWithStatus[]> => {
  try {
    const { data, error } = await supabase
      .from('events')
      .select('*')
      .order('start_date', { ascending: true });

    if (error) {
      console.error('Error fetching events:', error);
      throw error;
    }

    if (!data) return [];

    // Add status to each event
    const now = new Date();
    const eventsWithStatus: EventWithStatus[] = data.map((event: any) => {
      const startDate = new Date(event.start_date);
      const endDate = event.end_date ? new Date(event.end_date) : new Date(startDate);
      
      // Set end time to end of day if not specified
      if (event.end_date === null) {
        endDate.setHours(23, 59, 59);
      }

      let status: 'upcoming' | 'ongoing' | 'past';
      
      if (now < startDate) {
        status = 'upcoming';
      } else if (now >= startDate && now <= endDate) {
        status = 'ongoing';
      } else {
        status = 'past';
      }

      return {
        ...event,
        status
      };
    });

    return eventsWithStatus;
  } catch (error) {
    console.error('Error in fetchEvents:', error);
    return [];
  }
};

export const createEvent = async (event: Omit<Event, 'id' | 'created_at' | 'updated_at'>): Promise<Event | null> => {
  try {
    const { data, error } = await supabase
      .from('events')
      .insert(event as EventsInsert)
      .select()
      .single();

    if (error) {
      console.error('Error creating event:', error);
      throw error;
    }

    return data as unknown as Event;
  } catch (error) {
    console.error('Error in createEvent:', error);
    return null;
  }
};

export const updateEvent = async (id: string, event: Partial<Event>): Promise<Event | null> => {
  try {
    const { data, error } = await supabase
      .from('events')
      .update(event as Partial<EventsInsert>)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating event:', error);
      throw error;
    }

    return data as unknown as Event;
  } catch (error) {
    console.error('Error in updateEvent:', error);
    return null;
  }
};

export const deleteEvent = async (id: string): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('events')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting event:', error);
      throw error;
    }

    return true;
  } catch (error) {
    console.error('Error in deleteEvent:', error);
    return false;
  }
};
